import React, { useState, useEffect, useCallback } from 'react';
import { Smartphone, Check, X, AlertTriangle, RefreshCw } from 'lucide-react';
import { colors, FONT, radii } from '../../../theme';
import { S } from '../dashboardStyles';
import { SS } from './settingsStyles';
import { api } from '../../../services/api';
import { secureSyncStorage } from '../../../services/secureStore';
import {
  accountState, pendingDevices, authoriseDevice, revokeDevice, linkFingerprint, ACCOUNT_STATE,
} from '../../../services/deviceListBootstrap.mjs';

/**
 * The devices this account admits to owning, and the ones asking to be let in.
 *
 * The only screen in the app where a person does something no key can do for
 * them: compare eight characters here with eight characters on the phone in
 * their other hand. A server that substitutes a device of its own into the
 * request produces a fingerprint that does not match, and there is no other
 * way to find that out — which is why the comparison is stated as the thing
 * being asked for rather than tucked under a confirm button.
 */
export default function MyDevicesCard({ userId, thisDeviceId }) {
  const [state, setState] = useState(null);
  const [pending, setPending] = useState([]);
  const [listed, setListed] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(null);
  // Which row is being published right now. Publishing goes to the server and
  // back — through a relay, a second or two — and during it every button was
  // disabled with nothing to show for it, so the second device read as
  // «cannot be pressed» (03.09.2026).
  const [working, setWorking] = useState(null);

  const load = useCallback(async () => {
    setBusy(true); setError('');
    try {
      const st = await accountState({ api, storage: secureSyncStorage, accountId: userId, deviceId: thisDeviceId });
      setState(st);
      setListed(st.list ? st.list.devices : []);
      if (st.state === ACCOUNT_STATE.AUTHORISED || st.state === ACCOUNT_STATE.WAITING) {
        const p = await pendingDevices({ api, storage: secureSyncStorage, accountId: userId });
        setPending(p.pending);
      } else {
        setPending([]);
      }
    } catch (e) {
      setError(e?.message || 'Не удалось прочитать список устройств');
    } finally { setBusy(false); }
  }, [userId, thisDeviceId]);

  useEffect(() => { load(); }, [load]);

  const approve = async (d) => {
    setBusy(true); setWorking(d.deviceId); setError(''); setNote('');
    try {
      const r = await authoriseDevice({ api, storage: secureSyncStorage, accountId: userId, device: d });
      if (!r.ok) setError(r.reason);
      else { setNote(`Устройство добавлено, версия списка ${r.version}`); setConfirming(null); await load(); }
    } catch (e) { setError(e?.message || 'Не удалось подтвердить устройство'); }
    finally { setBusy(false); setWorking(null); }
  };

  const revoke = async (deviceId) => {
    setBusy(true); setError(''); setNote('');
    try {
      const r = await revokeDevice({ api, storage: secureSyncStorage, accountId: userId, deviceId });
      if (!r.ok) setError(r.reason);
      else { setNote(`Устройство убрано, версия списка ${r.version}`); await load(); }
    } catch (e) { setError(e?.message || 'Не удалось убрать устройство'); }
    finally { setBusy(false); }
  };

  const holdsMaster = state?.holdsMaster;

  return (
    <div style={S.settingsCard}>
      <div style={{ ...sectionTitle, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Smartphone size={16} color={colors.gray900} />
        <span>Устройства аккаунта</span>
        <button onClick={load} disabled={busy}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <RefreshCw size={15} color={colors.gray600} />
        </button>
      </div>

      <div style={SS.consequence}>
        Этот список подписан ключом самого аккаунта. Сервер может его хранить и отдавать,
        но не может составить — поэтому устройство, которого здесь нет, не сможет ни
        читать вашу переписку, ни писать от вашего имени.
      </div>

      {state?.state === ACCOUNT_STATE.WAITING && (
        <div style={{ ...SS.hint, background: 'rgba(220,120,40,0.14)', marginTop: 12 }}>
          <AlertTriangle size={18} color={colors.gray900} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <b>Это устройство ещё не подтверждено.</b> Откройте настройки на устройстве,
            где вы уже вошли, и подтвердите его там. Сверьте отпечаток{' '}
            <b style={{ fontFamily: 'monospace' }}>{linkFingerprint(thisDeviceId)}</b> — он должен
            совпасть с тем, что показано там.
          </span>
        </div>
      )}

      {state?.state === ACCOUNT_STATE.FOREIGN && (
        <div style={{ ...SS.hint, background: 'rgba(200,40,40,0.12)', marginTop: 12 }}>
          <AlertTriangle size={18} color={colors.gray900} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <b>Список устройств подписан незнакомым ключом.</b> Либо аккаунт начали заново
            на другом устройстве, либо кто-то выдаёт себя за него. Не переписывайтесь,
            пока не выясните это голосом.
          </span>
        </div>
      )}

      {pending.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ ...sectionTitle, marginBottom: 6 }}>Просятся в аккаунт</div>
          <div style={SS.consequence}>
            Прежде чем подтверждать — прочитайте отпечаток на самом устройстве и сверьте
            с тем, что написано здесь. Это единственная проверка, которую не может
            сделать за вас никакая криптография.
          </div>
          {pending.map(d => (
            <div key={d.deviceId} style={{
              display: 'flex', alignItems: 'center', gap: 10, marginTop: 10,
              padding: '10px 12px', background: colors.gray100, borderRadius: radii.sm,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: FONT, fontSize: 14, color: colors.gray900 }}>
                  {d.label || 'Устройство без метки'}
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 13, color: colors.gray600 }}>
                  {linkFingerprint(d.deviceId)}
                </div>
              </div>
              {holdsMaster ? (
                confirming === d.deviceId ? (
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    <button onClick={() => approve(d)} disabled={busy} style={approveBtn}>
                      <Check size={14} /> {working === d.deviceId ? 'Добавляю…' : 'Отпечаток совпал'}
                    </button>
                    <button onClick={() => setConfirming(null)} disabled={busy} style={ghostBtn}>Отмена</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirming(d.deviceId)} disabled={busy}
                    style={{ ...approveBtn, marginLeft: 'auto', opacity: busy ? 0.5 : 1 }}>
                    {busy ? 'подождите…' : 'Подтвердить'}
                  </button>
                )
              ) : (
                <span style={{ marginLeft: 'auto', ...SS.consequence }}>
                  подтвердить можно там, где лежит ключ аккаунта
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {listed.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ ...sectionTitle, marginBottom: 6 }}>
            Подтверждённые · версия списка {state?.version}
          </div>
          {listed.map(d => (
            <div key={d.deviceId} style={{
              display: 'flex', alignItems: 'center', gap: 10, marginTop: 8,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: FONT, fontSize: 14, color: colors.gray900 }}>
                  {d.label || 'Устройство без метки'}
                  {d.deviceId === thisDeviceId && (
                    <span style={{ ...SS.consequence, marginLeft: 6 }}>— это устройство</span>
                  )}
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 13, color: colors.gray600 }}>
                  {linkFingerprint(d.deviceId)}
                </div>
              </div>
              {holdsMaster && listed.length > 1 && d.deviceId !== thisDeviceId && (
                <button onClick={() => revoke(d.deviceId)} disabled={busy}
                  style={{ ...ghostBtn, marginLeft: 'auto' }}>
                  <X size={14} /> Убрать
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!holdsMaster && listed.length > 0 && (
        <div style={{ ...SS.consequence, marginTop: 12 }}>
          Ключ аккаунта лежит не здесь, поэтому добавлять и убирать устройства с этого
          экрана нельзя. Он есть в архиве переписки — если тот телефон потерян,
          восстановите архив, и ключ вернётся вместе с ним.
        </div>
      )}

      {error && <div style={{ ...SS.consequence, color: '#b23', marginTop: 10 }}>{error}</div>}
      {note && <div style={{ ...SS.consequence, marginTop: 10 }}>{note}</div>}
    </div>
  );
}

// Defined here rather than reached for in SS: a style key that does not exist
// renders as bare text over the screen, and neither the tests nor the build
// say a word about it. That has cost this project a release once already.
const sectionTitle = {
  fontFamily: FONT, fontSize: 13, fontWeight: 600, color: colors.gray900,
  textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8,
};
const approveBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', borderRadius: radii.sm,
  padding: '7px 12px', fontFamily: FONT, fontSize: 13, cursor: 'pointer',
  background: colors.gray900, color: colors.white,
};
const ghostBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: radii.sm,
  border: `1px solid ${colors.gray300}`, background: 'transparent',
  padding: '7px 12px', fontFamily: FONT, fontSize: 13, cursor: 'pointer', color: colors.gray900,
};
